/** Copyright © 2024 Qcells. All rights reserved.
This software is proprietary and confidential. Unauthorized use,
duplication, or distribution of software is strictly prohibited.
*/
import type { AxiosInstance } from 'axios';

import { Service } from '@hems/service/src/api/Service';
import { apiWrapper } from '@hems/service/src/util/apiHelper';
import { Helper } from '@hems/util';
import type { SettingYesNoCodeValue } from '@hems/util/src/constant/installation';
import type { StatusCodeValueType } from '@hems/util/src/exception/exception';

import type { ApiResponseWithStatus, BaseResponse, CommonAPIResponse, CommonResponseWrapper } from 'hems';

import type {
  MonitoringListSearchOptions,
  ErrorModuleInfo,
  MonitoringListSiteInfo,
  MonitoringListDeviceInfo,
  DeviceListDownLoadItem,
  CloudPasskeyResponse,
} from 'hems/device';
import type { BasicSiteInfoResponse } from 'hems/device/deviceinfo';
import type {
  ConfigurationRequestBody,
  ConfigurationResponse,
  AllDeviceGridCodeInfoResponse,
} from 'hems/device/settings';
import type { GridProfileData } from 'hems/device/settings/gridProfile';
import type {
  Installation,
  SaveHomeownerInfoParams,
  SaveLocationInfoParams,
  SiteTypeParams,
  SiteTypeResponse,
  SiteUpdateResponse,
} from 'hems/device/settings/installation';
import type { SiteInfo, SiteNetworkType } from 'hems/device/siteinfo';
import type { DeviceInstallation } from 'hems/install';

type GridCodeParam = {
  productModelNm?: string;
  cntryCd: string;
  grpCd?: string;
};

export default class DeviceService extends Service {
  constructor(axios: AxiosInstance) {
    super(axios);
  }

  /** Site 이미지 조회 */
  getImageBySiteId = async (siteId: number): Promise<File> => {
    return await this.get<File>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/images`),
      {},
      {
        responseType: 'blob',
      }
    );
  };

  /** Site 이미지 저장 */
  saveImageBySiteId = async (siteId: number, params: FormData): Promise<Response> => {
    const { data } = await this.put<CommonResponseWrapper<Response>>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/images`),
      params,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return data;
  };

  /** AC System - 모니터링 Site 목록 조회 */
  getMonitoringSiteList = async (params: Partial<MonitoringListSearchOptions>): Promise<MonitoringListSiteInfo[]> => {
    const { data } = await this.get<CommonResponseWrapper<MonitoringListSiteInfo[]>>(
      apiWrapper.stateApi('/devices/telemetries/multi-inverter/sites'),
      params
    );

    return data;
  };

  /**  AC System - 모니터링 EMS 목록 조회 */
  getMonitoringDeviceList = async (
    params: Partial<MonitoringListSearchOptions>
  ): Promise<MonitoringListDeviceInfo[]> => {
    const { data } = await this.get<CommonResponseWrapper<MonitoringListDeviceInfo[]>>(
      apiWrapper.stateApi('/devices/telemetries/multi-inverter/devices'),
      params
    );

    return data;
  };

  /** 모니터링 상세 - Dashboard - 프로파일, Inventory */
  getDeviceProfile = async (siteId: number): Promise<SiteInfo | null> => {
    const { data } = await this.get<CommonResponseWrapper<SiteInfo | null>>(
      apiWrapper.managerApi(`/devices/telemetries/${siteId}`)
    );

    return data;
  };

  /** 모니터링 상세 - System Settings - Installation */
  getInstallationInfo = async (siteId: number): Promise<Installation> => {
    const { data } = await this.get<CommonResponseWrapper<Installation>>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/installations`)
    );

    return data;
  };

  /**  Array & Mapping - Basic Device Info */
  getBasicDeviceInfo = async (siteId: string): Promise<BasicSiteInfoResponse> => {
    const { data } = await this.get<CommonResponseWrapper<BasicSiteInfoResponse>>(
      apiWrapper.managerApi(`/resources/sites/${siteId}/devices`)
    );

    return data;
  };

  /** 에러 모듈 목록 */
  getErrorDeviceList = async (siteId: number): Promise<ErrorModuleInfo[]> => {
    const { data } = await this.get<CommonResponseWrapper<ErrorModuleInfo[]>>(
      apiWrapper.managerApi(`/devices/alarms/${siteId}/modules`)
    );

    return data;
  };

  /** 장비 목록 엑셀 다운로드 */
  getDeviceList = async (): Promise<DeviceListDownLoadItem[]> => {
    const { data } = await this.get<CommonResponseWrapper<DeviceListDownLoadItem[]>>(
      apiWrapper.managerApi('/devices/profiles/excel-files')
    );

    return data;
  };

  /** 그리드 코드 정보 조회 */
  getGridCodeInfo = async (params: GridCodeParam): Promise<AllDeviceGridCodeInfoResponse> => {
    const { data } = await this.get<CommonResponseWrapper<AllDeviceGridCodeInfoResponse>>(
      apiWrapper.managerApi('/devices/profiles/grid-codes'),
      params
    );

    return data;
  };

  /** Site 위치 정보 저장 */
  saveLocationInfo = async (siteId: number, params: SaveLocationInfoParams): Promise<Response> => {
    const { data } = await this.put<CommonResponseWrapper<Response>>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/locations`),
      params
    );

    return data;
  };

  /** Site Home Owner 정보 저장 */
  saveHomeownerInfo = async (siteId: number, params: SaveHomeownerInfoParams): Promise<Response> => {
    const { data } = await this.put<CommonResponseWrapper<Response>>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/homeowners`),
      params
    );

    return data;
  };

  /** Site Off Grid 여부 저장 */
  updateOffGrid = async (siteId: number, params: { offGrid: SettingYesNoCodeValue }): Promise<Response> => {
    const { data } = await this.put<CommonResponseWrapper<Response>>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/off-grids`),
      params
    );

    return data;
  };

  /** Site 위치 중복 체크 */
  checkDuplicatedLocation = async (params: { latitude: number; longitude: number }): Promise<boolean> => {
    const { data } = await this.get<CommonResponseWrapper<boolean>>(
      apiWrapper.managerApi('/devices/profiles/locations/validations'),
      params
    );

    return data;
  };

  /** Site 메모 저장 */
  updateComments = async (siteId: number, params: { memo: string | null }): Promise<Response> => {
    const { data } = await this.put<CommonResponseWrapper<Response>>(
      apiWrapper.managerApi(`/devices/profiles/${siteId}/comments`),
      params
    );

    return data;
  };

  /** Site 설치 Step 조회 */
  getInstallationSteps = async (siteId: number): Promise<{ siteId: string; stepData: DeviceInstallation }> => {
    return await this.get<CommonResponseWrapper<{ siteId: string; stepData: DeviceInstallation }>>(
      apiWrapper.managerApi(`/managements/mobiles/pro-apps/${siteId}/steps`)
    ).then((res) => {
      const data = res.data as unknown as { siteId: string; stepData: string };

      return {
        ...data,
        stepData: data?.stepData ? (Helper.isJSON(data.stepData) ? JSON.parse(data.stepData) : null) : null,
      };
    });
  };

  /** Site 네트워크 타입 조회 */
  getSiteNetworkType = async (siteId: number): Promise<SiteNetworkType> => {
    const { data } = await this.get<CommonResponseWrapper<SiteNetworkType>>(
      apiWrapper.stateApi(`/devices/telemetries/network-type/${siteId}`)
    );

    return data;
  };

  /** Site 부가 정보(siteType, displayMapperFlag) 조회 */
  getSite = async (siteId: number): Promise<SiteTypeResponse> => {
    const { data } = await this.get<BaseResponse<SiteTypeResponse>>(apiWrapper.managerApiV1(`/sites/${siteId}`));

    return data;
  };

  /** Site 정보(siteType, displayMapperFlag, installerId) 변경 */
  updateSite = async (
    siteId: number,
    params: SiteTypeParams
  ): Promise<{ data: SiteUpdateResponse; status: StatusCodeValueType }> =>
    this.patchNew<SiteUpdateResponse>(apiWrapper.managerApiV1(`/sites/${siteId}`), params);

  /** Grid Profile Report API (pdf data). 응답: { context: {}, data: GridProfileData } */
  getSiteGridProfile = async (siteId: number, customGridProfile: string): Promise<BaseResponse<GridProfileData>> =>
    this.get<BaseResponse<GridProfileData>>(apiWrapper.managerApiV1(`/sites/${siteId}/grid-profile`), {
      customGridProfile,
    });

  /** Basic, Engineer, Advanced Setting 조회 */
  getConfiguration = async (deviceId: string): Promise<ConfigurationResponse> => {
    const { data } = await this.get<BaseResponse<ConfigurationResponse>>(
      apiWrapper.managerApiV1(`/devices/${deviceId}/configuration-state`)
    );

    return data;
  };

  /** Basic, Engineer, Advanced Setting 저장 */
  saveConfiguration = (deviceId: string, requestBody: ConfigurationRequestBody): Promise<ApiResponseWithStatus> =>
    this.postNew<CommonAPIResponse>(apiWrapper.managerApiV1(`/devices/${deviceId}/configuration`), requestBody);

  /** Cloud Passkey 조회 */
  getCloudPasskey = async (deviceId: string): Promise<CloudPasskeyResponse> => {
    const { data } = await this.get<CommonResponseWrapper<CloudPasskeyResponse>>(
      apiWrapper.managerApiV1(`/devices/${deviceId}/password`)
    );

    return data;
  };
}

export { DeviceService };
